import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { trackChangeAmplitude } from '../../analytics/thunks/track-change-amplitude.thunk';
import * as stripActions from '../actions/strip.actions';
import { usePlot } from '../contexts/plot.context';
import { useStrip } from '../hooks/useStrip';
import { stripGainSelector } from '../selectors/strip.selectors';
import { isOfTypeChangeAmplitude, squareToPixel } from '../utils/graph.utils';
import { GenericPicker } from './GenericPicker';
import { Box } from '@cdl/heartbeat-react';

const GAIN_PICK_HEIGHT = squareToPixel(1.5);
interface Props {
  leadsShowing?: boolean;
}

export const GainPickerContainer: React.FC<Props> = ({
  leadsShowing = false,
}) => {
  const dispatch = useDispatch();
  const { plotName } = usePlot();
  const { stripId, width, height } = useStrip();
  const gain = useSelector(stripGainSelector(stripId));

  const onChange = (value: 1 | -1) => {
    // we want to change the gain only in the range [0.125, 16]
    const limitsCondition =
      gain * 2 ** value >= 0.125 && gain * 2 ** value <= 16;

    if (isOfTypeChangeAmplitude(plotName)) {
      const newAmplitude = limitsCondition ? gain * 2 ** value : gain;
      dispatch(trackChangeAmplitude(newAmplitude, gain, plotName));
    }

    if (gain * 2 ** value >= 0.125 && gain * 2 ** value <= 16) {
      dispatch(stripActions.incrementGain({ stripId, value }));
    }
  };

  /**
   * The height for the GainPickerContainer should be:
   * if (LeadPickerContainer) {
   *  GainPickerContainer ---> (height / 2) - GAIN_PICK_HEIGHT;
   * } else {
   *  GainPickerContainer ---> (height / 2) - (GAIN_PICK_HEIGHT / 2);
   * }
   */
  const gainPickerPos = leadsShowing
    ? height / 2 - GAIN_PICK_HEIGHT
    : height / 2 - GAIN_PICK_HEIGHT / 2;
  return (
    <foreignObject
      width={width}
      height={GAIN_PICK_HEIGHT}
      y={gainPickerPos}
      overflow='visible'
    >
      <Box pl='xs' width='5.2rem'>
        <GenericPicker title={`x${gain}`} onChange={onChange} />
      </Box>
    </foreignObject>
  );
};
